import React from 'react';
import Reveal from './Reveal';
import { CATEGORIES, byCategory } from '../assets/catalogueData';
import { CATALOGUE_BLUR } from '../assets/catalogueBlur';
import { trackEvent } from '../analytics';
import { ArrowUpRight, ArrowRight, Plus, Minus } from 'lucide-react';
import './Catalogue.css';

const INITIAL_COUNT = 5;
const CATEGORY_COUNTS = Object.fromEntries(CATEGORIES.map((category) => [category.key, byCategory(category.key).length]));

// Shared by Home and the narrower Custom Interiors body. Full design copy
// stays in DesignView; the browse view gives the photography room to breathe.
const Catalogue = ({ onOpen, initialCategory, askLabel, showIntro = true }) => {
  const startKey = CATEGORIES.some((category) => category.key === initialCategory) ? initialCategory : CATEGORIES[0].key;
  const [active, setActive] = React.useState(startKey);
  const [expanded, setExpanded] = React.useState(false);
  const seenRef = React.useRef(new Set());
  const gridRef = React.useRef(null);
  const gridId = React.useId();
  const selectId = React.useId();
  const items = byCategory(active);
  const visible = expanded ? items : items.slice(0, INITIAL_COUNT);
  const activeLabel = CATEGORIES.find((category) => category.key === active)?.label;
  const askName = askLabel && active === startKey ? askLabel : activeLabel;

  React.useEffect(() => {
    const element = gridRef.current;
    if (!element || seenRef.current.has(active)) return;
    const recordView = () => {
      seenRef.current.add(active);
      trackEvent('catalogue_view', { category: active });
    };
    if (!('IntersectionObserver' in window)) { recordView(); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        recordView();
        observer.disconnect();
      }
    }, { threshold: 0 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [active]);

  const selectCategory = (key) => {
    setActive(key);
    setExpanded(false);
  };

  return (
    <section className={`catalogue catalogue--editorial${showIntro ? '' : ' catalogue--embedded'}`}
      id="catalogue" data-testid="catalogue-section" aria-label="FairFix design catalogue">
      <div className="container">
        <div className="showroom-content">
          {showIntro && (
            <Reveal as="header" className="catalogue-intro">
              <div>
                <p className="catalogue-eyebrow">The showroom</p>
                <h2 className="catalogue-title">Picture the <span className="h-accent">possibilities.</span></h2>
              </div>
              <p className="catalogue-sub">Ideas for your home, inside and out. Explore a design, then let&rsquo;s talk about making it suit your space.</p>
            </Reveal>
          )}

          <div className="showroom-filters">
            <div className="showroom-chips" role="group" aria-label="Filter designs by category" data-testid="showroom-chips">
              {CATEGORIES.map((category) => (
                <button key={category.key} type="button"
                  className={`showroom-chip${active === category.key ? ' showroom-chip--active' : ''}`}
                  aria-pressed={active === category.key} aria-controls={gridId}
                  data-testid={`showroom-chip-${category.key}`} onClick={() => selectCategory(category.key)}>
                  {category.label}<span aria-hidden="true">{CATEGORY_COUNTS[category.key]}</span>
                </button>
              ))}
            </div>
            <div className="showroom-select">
              <label htmlFor={selectId}>Explore by category</label>
              <select id={selectId} value={active} onChange={(event) => selectCategory(event.target.value)}
                aria-controls={gridId} data-testid="showroom-category-select">
                {CATEGORIES.map((category) => (
                  <option key={category.key} value={category.key}>{category.label} ({CATEGORY_COUNTS[category.key]})</option>
                ))}
              </select>
            </div>
          </div>

          <div className="showroom-collection-heading">
            <h3>{activeLabel}</h3>
            <p>Design inspiration <span aria-hidden="true">/</span> Select an image to explore</p>
          </div>
          <div ref={gridRef} id={gridId} className="showroom-grid" data-rail={active}
            data-testid={`catalogue-rail-${active}`}>
            {visible.map((design, index) => {
              const featured = index === 0;
              // The service-page instance is bounded to 720px by its parent.
              const sizes = showIntro
                ? (featured
                  ? '(max-width: 767px) calc(100vw - 44px), (max-width: 1019px) calc(100vw - 80px), 628px'
                  : '(max-width: 523px) calc(100vw - 44px), (max-width: 1019px) 45vw, 302px')
                : (featured
                  ? '(max-width: 767px) calc(100vw - 84px), 676px'
                  : '(max-width: 563px) calc(100vw - 84px), 326px');
              return (
                <Reveal as="button" key={design.id} type="button"
                  delay={Math.min(index % INITIAL_COUNT * 65, 260)}
                  className={`catalogue-card${featured ? ' catalogue-card--featured' : ''}`}
                  data-testid={`catalogue-card-${design.id}`} aria-label={`View design: ${design.name}`}
                  aria-haspopup="dialog" onClick={() => onOpen(design)}>
                  <span className="catalogue-card-media"
                    style={CATALOGUE_BLUR[design.id] ? { backgroundImage: `url("${CATALOGUE_BLUR[design.id]}")` } : undefined}>
                    <picture>
                      <source type="image/webp" srcSet={`${design.src}-400.webp 400w, ${design.src}-800.webp 800w, ${design.src}-1600.webp 1600w`} sizes={sizes} />
                      <img src={`${design.src}-800.jpg`}
                        srcSet={`${design.src}-400.jpg 400w, ${design.src}-800.jpg 800w, ${design.src}-1600.jpg 1600w`}
                        sizes={sizes} width="800" height={Math.round(800 * design.h / design.w)}
                        alt="" loading="lazy" decoding="async" className="catalogue-card-img" />
                    </picture>
                    <span className="showroom-image-open" aria-hidden="true"><ArrowUpRight size={20} strokeWidth={1.6} /></span>
                  </span>
                  <span className="catalogue-card-body">
                    <span className="showroom-design-number" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                    <span className="catalogue-card-title">{design.name}</span>
                  </span>
                </Reveal>
              );
            })}
          </div>

          <div className="showroom-foot">
            <p className="showroom-counter" data-testid="showroom-counter" aria-live="polite" aria-atomic="true">
              {`Showing ${visible.length} of ${items.length} ideas`}
            </p>
            {items.length > INITIAL_COUNT && (
              <button type="button" className="showroom-more-btn" data-testid="showroom-show-more"
                aria-expanded={expanded} aria-controls={gridId} onClick={() => setExpanded((value) => !value)}>
                {expanded ? <Minus size={16} aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
                {expanded ? 'Show fewer ideas' : `View all ${items.length} ideas`}
              </button>
            )}
          </div>

          <div className="showroom-enquiry">
            <div><h3>Have something else in mind?</h3><p>Bring a photo, a sketch, or an idea. We can work through it together.</p></div>
            <button type="button" className="showroom-enquiry-btn" data-testid={`catalogue-ask-${active}`}
              aria-haspopup="dialog" onClick={() => onOpen({ __ask_category: active, __label: askName })}>
              {`Talk about ${askName.toLowerCase()}`}<ArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Catalogue;
